<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_STANDING_ORDERS')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="standingOrderRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="standingOrderList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(amount)="data">
        {{ toBalance(data.value) }}
      </template>
      <template #cell(startDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(endDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(dateCreatedFinancial)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(lastTriggerDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(nextTriggerDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(computationType)="data">
        {{ data.value ? addSpaceToString(data.value) : '' }}
      </template>
      <template #cell(type)="data">
        {{ data.value ? addSpaceToString(data.value) : '' }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template
        #cell(actions)="data"
      >
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_STANDING_ORDERS')"
              @click="onSelectStandingOrderItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectStandingOrderItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="data.item.status !== 'Expired' && getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_STANDING_ORDERS' : 'ACTIVATE_STANDING_ORDERS')"
              @click="standingOrderStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ data.item.status === 'Active' && data.item.status === 'Active' ? $t('Deactivate') : $t('Activate') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      :ok-disabled="isLoading"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="standingOrderRules">
        <b-form>
          <b-row class="match-height">
            <!-- Name -->
            <b-col md="6">
              <InputField
                id="name"
                v-model="standingOrderFormData.description"
                :label="$t('Order Name')"
                :placeholder="$t('Enter order name')"
                type="text"
                :error-name="$t('Order Name')"
                rules="required"
                for="name"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Name -->
            <b-col
              md="6"
            >
              <SelectField
                id="order-type"
                v-model="standingOrderFormData.type"
                :label="$t('Order Type')"
                :placeholder="$t('Select')"
                :options="translatedOrderTypeOptions"
                :reduce="option => option.value"
                :error-name="$t('Order Type')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :set-selected="setOrderType"
              />
            </b-col>
            <b-col
              md="6"
            >
              <SelectField
                id="transaction-type"
                v-model="standingOrderFormData.computationType"
                :label="$t('Transaction Type')"
                :placeholder="$t('Select')"
                :options="translatedTransactionTypeOptions"
                :reduce="option => option.value"
                :error-name="$t('Transaction Type')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :set-selected="setTransactionType"
              />
            </b-col>

            <b-col md="6">
              <InputNumberField
                id="amount"
                v-model="standingOrderFormData.amount"
                :label="$t('Amount')"
                :placeholder="$t('Enter amount')"
                type="number"
                :error-name="$t('Amount')"
                :rules="standingOrderAmount"
                :readonly="transactionType ? transactionType === 'Percentage' : isReadonly"
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="percentage-value"
                v-model="standingOrderFormData.percentageValue"
                :label="$t('Percentage Value')"
                :placeholder="$t('Enter percentage value')"
                type="number"
                :error-name="$t('Percentage Value')"
                :rules="`${standingOrderPecentage}|positive|upTo4Decimal|max-value:100`"
                for="percentage-value"
                :readonly="transactionType ? transactionType === 'Flat' : isReadonly"
              />
            </b-col>

            <b-col
              md="6"
            >
              <SelectField
                id="how-long"
                v-model="standingOrderFormData.forHowLong"
                :label="$t('For How Long?')"
                :placeholder="$t('Select')"
                :options="translatedHowLongOptions"
                :reduce="option => option.value"
                :error-name="$t('For How Long')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :set-selected="setForHowLong"
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="order-triggered-every"
                v-model="standingOrderFormData.sweepEvery"
                :label="$t('Order To Be Triggered Every')"
                :placeholder="$t('Enter order to be triggered every')"
                type="number"
                :error-name="$t('Order To Be Triggered Every')"
                rules="required|integer"
                for="order-triggered-every"
                :readonly="isReadonly"
                @input="getComputedTenure"
              />
            </b-col>
            <b-col
              md="6"
            >
              <SelectField
                id="order-frequency"
                v-model="standingOrderFormData.frequency"
                :label="$t('Order Frequency')"
                :placeholder="$t('Select')"
                :options="translatedOrderFrequencyOptions"
                :reduce="option => option.value"
                :error-name="$t('Order Frequency')"
                rules="required"
                item="label"
                :readonly="isReadonly"
                :set-selected="setOrderFrequency"
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="over-many-cycles"
                v-model="standingOrderFormData.overHowManyCycles"
                :label="$t('Over How Many Cycles')"
                :placeholder="$t('Enter over how many cycles')"
                type="number"
                :error-name="$t('Over How Many Cycles')"
                rules="required|integer"
                for="over-many-cycles"
                :readonly="isReadonly"
                @input="getComputedTenure"
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="tenure"
                v-model="standingOrderFormData.tenure"
                :label="$t('Tenure')"
                :placeholder="$t('Enter tenure')"
                type="number"
                :error-name="$t('Tenure')"
                rules="required"
                readonly
              />
            </b-col>

            <b-col md="6">
              <DatePickerField
                id="start-date"
                v-model="standingOrderFormData.startDate"
                rules="required"
                :label="$t('Start Date')"
                format="Y-m-d"
                :error-name="$t('Start Date')"
                :config="datePickerConfig"
                :disabled="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <DatePickerField
                id="end-date"
                v-model="standingOrderFormData.endDate"
                :label="$t('End Date')"
                format="Y-m-d"
                :rules="standingOrderEndDate"
                :error-name="$t('End Date')"
                :config="datePickerConfig"
                :disabled="forHowLongStatus ? forHowLongStatus === 'Continuous' : isReadonly"
              />
            </b-col>
          </b-row>

          <b-row
            v-if="accountOrderType === 'CASA-CASA'"
            class="match-height"
          >
            <b-col md="6">
              <InputField
                id="debit-account"
                v-model="standingOrderFormData.debitAccount"
                :label="$t('Debit Account')"
                :placeholder="$t('Search CASA')"
                type="text"
                :error-name="$t('Debit Account')"
                rules="required"
                :readonly="isReadonly"
                :on-input="onViewCustomerDebitSearchModal"
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="credit-account"
                v-model="standingOrderFormData.creditAccount"
                :label="$t('Credit Account')"
                :placeholder="$t('Search CASA')"
                type="text"
                :error-name="$t('Credit Account')"
                rules="required"
                :readonly="isReadonly"
                :on-input="onViewCustomerCreditSearchModal"
              />
            </b-col>
          </b-row>
          <b-row
            v-if="accountOrderType === 'CASA-GL'"
            class="match-height"
          >
            <b-col
              md="6"
              class="mt-0 mb-1"
            >
              <b-form-radio-group
                v-model="accountTypeCasaGl"
                :options="translatedAccountTypeListOptions"
                class="demo-inline-spacing"
                :name="`radios-stacked-casa-gl`"
                stacked
                value-field="value"
                text-field="text"
                disabled-field="disabled"
              />
              <b-col
                v-if="accountTypeCasaGl === 'SavingsAndCurrent'"
                class="mt-2"
                md="12"
              >
                <InputField
                  id="debit-account"
                  v-model="standingOrderFormData.debitAccount"
                  :label="$t('Debit Account')"
                  :placeholder="$t('Search CASA')"
                  type="text"
                  :error-name="$t('Debit Account')"
                  rules="required"
                  :readonly="isReadonly"
                  :on-input="onViewCustomerDebitSearchModal"
                />
              </b-col>
              <b-col
                v-else
                class="mt-2"
                md="12"
              >
                <SelectField
                  id="debit-account"
                  v-model="standingOrderFormData.debitAccount"
                  :label="$t('Debit Account')"
                  :placeholder="$t('Select GL')"
                  :options="glAccountOptions"
                  :reduce="option => option.value"
                  :error-name="$t('Debit Account')"
                  rules="required"
                  item="label"
                  :loading="isLoadingGlAccount"
                  :readonly="isReadonly"
                />
              </b-col>

            </b-col>
            <b-col
              md="6"
              class="mt-0 mb-1"
            >
              <b-form-radio-group
                v-model="accountTypeGlCasa"
                :options="translatedAccountTypeListOptions"
                class="demo-inline-spacing"
                :name="`radios-stacked-gl-casa`"
                stacked
                value-field="value"
                text-field="text"
                disabled-field="disabled"
              />
              <b-col
                v-if="accountTypeGlCasa === 'GeneralLedgerAccount'"
                class="mt-2"
                md="12"
              >
                <SelectField
                  id="credit-account"
                  v-model="standingOrderFormData.creditAccount"
                  :label="$t('Credit Account')"
                  :placeholder="$t('Select GL')"
                  :options="glAccountOptions"
                  :reduce="option => option.value"
                  :error-name="$t('Credit Account')"
                  rules="required"
                  item="label"
                  :loading="isLoadingGlAccount"
                  :readonly="isReadonly"
                />

              </b-col>
              <b-col
                v-else
                class="mt-2"
                md="12"
              >
                <InputField
                  id="credit-account"
                  v-model="standingOrderFormData.creditAccount"
                  :label="$t('Credit Account')"
                  :placeholder="$t('Search CASA')"
                  type="text"
                  :error-name="$t('Credit Account')"
                  rules="required"
                  :readonly="isReadonly"
                  :on-input="onViewCustomerCreditSearchModal"
                />
              </b-col>
            </b-col>
          </b-row>
          <b-row
            v-if="accountOrderType === 'GL-GL'"
            class="match-height"
          >
            <b-col md="6">
              <SelectField
                id="debit-account"
                v-model="standingOrderFormData.debitAccount"
                :label="$t('Debit Account')"
                :placeholder="$t('Select GL')"
                :options="glAccountOptions"
                :reduce="option => option.value"
                :error-name="$t('Debit Account')"
                rules="required"
                item="label"
                :loading="isLoadingGlAccount"
                :readonly="isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="credit-account"
                v-model="standingOrderFormData.creditAccount"
                :label="$t('Credit Account')"
                :placeholder="$t('Select GL')"
                :options="glAccountOptions"
                :reduce="option => option.value"
                :error-name="$t('Credit Account')"
                rules="required"
                item="label"
                :loading="isLoadingGlAccount"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row>

          <b-row
            v-if="accountOrderType === 'Default'"
            class="match-height"
          >
            <b-col md="6">
              <InputField
                id="debit-account"
                v-model="standingOrderFormData.debitAccount"
                :label="$t('Debit Account')"
                :placeholder="$t('Search CASA')"
                type="text"
                :error-name="$t('Debit Account')"
                rules="required"
                :readonly="isReadonly"
                :on-input="onViewCustomerDebitSearchModal"
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="credit-account"
                v-model="standingOrderFormData.creditAccount"
                :label="$t('Credit Account')"
                :placeholder="$t('Search CASA')"
                type="text"
                :error-name="$t('Credit Account')"
                rules="required"
                :readonly="isReadonly"
                :on-input="onViewCustomerCreditSearchModal"
              />
            </b-col>
          </b-row>

          <b-row
            class="match-height"
          >
            <b-col md="6">
              <SelectField
                id="debit-branch"
                v-model="standingOrderFormData.debitBranchCode"
                :label="$t('Debit Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                :reduce="option => option.value"
                :error-name="$t('Debit Branch')"
                rules="required"
                item="label"
                :readonly="isDebitCasaBranch ? isDebitCasaBranch : isReadonly"
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="credit-branch"
                v-model="standingOrderFormData.creditBranchCode"
                :label="$t('Credit Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                :reduce="option => option.value"
                :error-name="$t('Credit Branch')"
                rules="required"
                item="label"
                :readonly="isCreditCasaBranch ? isCreditCasaBranch : isReadonly"
              />
            </b-col>
          </b-row>
          <b-row
            class="match-height"
          >
            <b-col md="6">
              <InputField
                id="debit-narration"
                v-model="standingOrderFormData.debitNarration"
                :label="$t('Debit Narration')"
                :placeholder="$t('Enter debit narration')"
                type="text"
                :error-name="$t('Debit Narration')"
                rules="required"
                :readonly="isReadonly"
                @input="getNarration"
              />
            </b-col>
            <b-col md="6">
              <InputField
                id="credit-narration"
                v-model="standingOrderFormData.creditNarration"
                :label="$t('Credit Narration')"
                :placeholder="$t('Enter credit narration')"
                type="text"
                :error-name="$t('Credit Narration')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row>
          <!-- <b-row class="match-height">
            <b-col md="6">
              <SwitchButton
                v-model="standingOrderFormData.forceDebit"
                label="Allow Force Debit"
                name="allow-force-debit"
                class="mt-2"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row> -->
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchStandingOrder"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <b-col md="6">
          <InputField
            id="description"
            v-model="filter.description"
            :label="$t('Order Name')"
            :placeholder="$t('Enter description')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="debit-account"
            v-model="filter.debitAccount"
            :label="$t('Debit Account Number')"
            :placeholder="$t('Enter debit account')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="debit-account-name"
            v-model="filter.debitAccountName"
            :label="$t('Debit Account Name')"
            :placeholder="$t('Enter debit account name')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="credit-account"
            v-model="filter.creditAccount"
            :label="$t('Credit Account Number')"
            :placeholder="$t('Enter credit account')"
            type="text"
          />
        </b-col>
        <b-col md="6">
          <InputField
            id="credit-account-name"
            v-model="filter.creditAccountName"
            :label="$t('Credit Account Name')"
            :placeholder="$t('Enter credit account name')"
            type="text"
          />
        </b-col>

        <b-col md="6">
          <SelectField
            id="order-type"
            v-model="filter.type"
            :label="$t('Order Type')"
            :reduce="option=>option.value"
            :placeholder="$t('Select')"
            :options="translatedOrderTypeOptions"
            item="label"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="order-type"
            v-model="filter.computationType"
            :label="$t('Transaction Type')"
            :reduce="option=>option.value"
            :placeholder="$t('Select')"
            :options="translatedTransactionTypeOptions"
            item="label"
          />
        </b-col>
        <b-col md="6">
          <SelectField
            id="frequency"
            v-model="filter.frequency"
            :label="$t('Order Frequency')"
            :reduce="option=>option.value"
            :placeholder="$t('Select')"
            :options="translatedOrderFrequencyOptions"
            item="label"
          />
        </b-col>

        <b-col md="6">
          <DatePickerField
            id="start-date"
            v-model="filter.startDate"
            :label="$t('Start Date')"
            format="Y-m-d"
            :config="datePickerConfig"
          />
        </b-col>
        <b-col md="6">
          <DatePickerField
            id="end-date"
            v-model="filter.endDate"
            :label="$t('End Date')"
            format="Y-m-d"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :reduce="option=>option.value"
            :placeholder="$t('Select')"
            :options="translatedStatusOptions"
            item="label"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <b-modal
      id="customer-debit-search-modal"
      :ok-title="$t('Search')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="sm"
      :title="$t('Search Account')"
      modal-footer
      @ok="handleDebitSearch"
    >
      <validation-observer ref="searchRules">
        <b-form @submit.prevent="handleDebitSearch">
          <b-row>
            <Alert
              :show="showFormErrorAlert"
              :message="$t('Customer\'s account name or account number or customer id is required')"
            />

            <b-col
              md="12"
              class="mt-3"
            >
              <InputField
                id="account-number"
                v-model="searchParams.accountNumber"
                :label="$t('Customer Account Number')"
                :placeholder="$t('Enter account number')"
                type="text"
                for="account-number"
              />
            </b-col>
            <b-col md="12">
              <InputField
                id="account-name"
                v-model="searchParams.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter account name')"
                type="text"
                error-name=""
                rules=""
              />
            </b-col>
            <b-col
              md="12"
            >
              <InputField
                id="customer-id"
                v-model="searchParams.customerID"
                :label="$t('Customer ID')"
                :placeholder="$t('Enter customer id')"
                type="text"
                for="customer-id"
              />
            </b-col>
            <b-col md="12">
              <InputField
                id="reference-number"
                v-model="searchParams.referenceNumber"
                :label="$t('Reference Number')"
                :placeholder="$t('Enter reference number')"
                type="text"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
    <b-modal
      id="customer-credit-search-modal"
      :ok-title="$t('Search')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="sm"
      :title="$t('Search Account')"
      modal-footer
      @ok="handleCreditSearch"
    >
      <validation-observer ref="searchRules">
        <b-form @submit.prevent="handleCreditSearch">
          <b-row>
            <Alert
              :show="showFormErrorAlert"
              :message="$t('Customer\'s account name or account number or customer id is required')"
            />

            <b-col
              md="12"
              class="mt-3"
            >
              <InputField
                id="account-number"
                v-model="searchParams.accountNumber"
                :label="$t('Customer Account Number')"
                :placeholder="$t('Enter account number')"
                type="text"
                for="account-number"
              />
            </b-col>
            <b-col md="12">
              <InputField
                id="account-name"
                v-model="searchParams.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter account name')"
                type="text"
                error-name=""
                rules=""
              />
            </b-col>
            <b-col
              md="12"
            >
              <InputField
                id="customer-id"
                v-model="searchParams.customerID"
                :label="$t('Customer ID')"
                :placeholder="$t('Enter customer id')"
                type="text"
                for="customer-id"
              />
            </b-col>
            <b-col md="12">
              <InputField
                id="reference-number"
                v-model="searchParams.referenceNumber"
                :label="$t('Reference Number')"
                :placeholder="$t('Enter reference number')"
                type="text"
              />
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
    <b-modal
      id="search-debit-modal"
      ref="search-debit-modal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Customer Account Details')"
    >
      <b-table
        ref="customerListTable"
        select-mode="single"
        :items="customerList"
        :per-page="pageSize"
        :current-page="currentPage"
        responsive
        :fields="translatedTableSearchHeaders"
        primary-key="id"
        selectable
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
        @row-selected="onSelectDebitCustomer"
      >
        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>
      </b-table>

      <TablePagination
        v-model="currentPage"
        :from="from"
        :to="to"
        :total-rows="totalEntries"
        :per-page="perPage"
      />

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>

    <b-modal
      id="search-credit-modal"
      ref="search-credit-modal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Customer Account Details')"
    >
      <b-table
        ref="customerListTable"
        select-mode="single"
        :items="customerList"
        :per-page="pageSize"
        :current-page="currentPage"
        responsive
        :fields="translatedTableSearchHeaders"
        primary-key="id"
        selectable
        show-empty
        :empty-text="$t('No result found')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
        @row-selected="onSelectCreditCustomer"
      >
        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>
      </b-table>

      <TablePagination
        v-model="currentPage"
        :from="from"
        :to="to"
        :total-rows="totalEntries"
        :per-page="perPage"
      />

      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BBadge, BForm, BFormRadioGroup, BDropdownItem, BDropdown,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import Alert from '../../components/alert/Alert.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../forms/InputField.vue'
import InputNumberField from '../../forms/InputNumberField.vue'
import SelectField from '../../forms/SelectField.vue'
import getStatusColor from '../../../helpers/getStatusColor'
import Button from '../../components/button/Button.vue'
import { accountPermissions } from '../../../helpers/permissions'
import DatePickerField from '../../forms/DatePickerField.vue'
// import SwitchButton from '../../components/switch/Switch.vue'
import addSpaceToString from '../../../helpers/addSpaceToString'
import {
  getDaysInMonth, getDaysInYear, toDateWithYear, toYearWithDate,
} from '../../../helpers/formatDate'
import { toKobo, toBalance } from '../../../helpers/formatAmount'

// import { modalBus } from '../../../main'
const orderTypeOptions = [
  { value: 'CasaAndCasa ', label: 'CASA and CASA' },
  { value: 'CasaAndGL', label: 'CASA and GL' },
  { value: 'GLANDGL', label: 'GL and GL' },
]
const transactionTypeOptions = [
  { value: 'Flat', label: 'Flat Amount' },
  { value: 'Percentage', label: 'Percentage' },
  { value: 'FlatAndPercentage', label: 'Flat + Percentage' },
]
const howLongOptions = [
  { value: 'Continuous', label: 'Continuous' },
  { value: 'NonContinuous', label: 'Non Continuous' },
]
const orderFrequencyOptions = [
  { value: 'Day', label: 'Days' },
  { value: 'Week', label: 'Weeks' },
  { value: 'Month', label: 'Months' },
  { value: 'Year', label: 'Years' },
]
const statusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'InActive', value: 'InActive' },
  { label: 'Paused', value: 'Paused' },
  { label: 'Disabled', value: 'Disabled' },
  { label: 'Expired', value: 'Expired' },
]
const accountTypeListOptions = [
  { text: 'Savings/Current', value: 'SavingsAndCurrent', disabled: false },
  { text: 'General Ledger', value: 'GeneralLedgerAccount', disabled: false },
]
export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BModal,
    BForm,
    BBadge,
    BSpinner,
    SelectField,
    TablePagination,
    TableEntries,
    ValidationObserver,
    InputField,
    InputNumberField,
    BDropdownItem,
    BDropdown,
    FullScreenSpinner,
    Button,
    DatePickerField,
    // SwitchButton,
    Alert,
    BFormRadioGroup,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      addSpaceToString,
      toDateWithYear,
      toBalance,
      isViewOnly: false,
      isReadonly: false,
      isEditable: false,
      buttonLabel: this.$t('Save'),
      sortBy: 'description',
      required,
      modalTitle: this.$t('Create A Standing Order'),
      sortDesc: false,
      showSpinner: false,
      isCreditCasaBranch: false,
      isDebitCasaBranch: false,
      getStatusColor,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'description', sortable: true, label: 'Order Name' },
        { key: 'type', sortable: true, label: 'Order Type' },
        { key: 'computationType', sortable: true, label: 'Transaction Type' },
        { key: 'debitAccount', sortable: true, label: 'Debit Account Number' },
        { key: 'debitAccountName', sortable: true, label: 'Debit Account Name' },
        { key: 'creditAccount', sortable: true, label: 'Credit Account Number' },
        { key: 'creditAccountName', sortable: true, label: 'Credit Account Name' },
        { key: 'amount', sortable: true, label: 'Amount' },
        { key: 'percentageValue', sortable: true, label: 'Percentage' },
        { key: 'startDate', sortable: true, label: 'Start Date' },
        { key: 'endDate', sortable: true, label: 'End Date' },
        { key: 'tenure', sortable: true, label: 'Tenure' },
        { key: 'lastTriggerDate', sortable: true, label: 'Last Trigger Date' },
        { key: 'nextTriggerDate', sortable: true, label: 'Next Trigger Date' },
        { key: 'dateCreatedFinancial', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions' },
      ],
      standingOrderFormData: {
        type: '',
        debitAccount: '',
        creditAccount: '',
        computationType: '',
        amount: 0,
        percentageValue: 0,
        forHowLong: '',
        frequency: '',
        sweepEvery: 0,
        overHowManyCycles: 0,
        tenure: 0,
        startDate: null,
        endDate: null,
        debitNarration: '',
        creditNarration: '',
        debitBranchCode: '',
        creditBranchCode: '',
        description: '',
        // forceDebit: true,
        id: '',
      },
      filter: {
        description: '',
        debitAccount: '',
        debitAccountName: '',
        creditAccount: '',
        creditAccountName: '',
        type: '',
        computationType: '',
        status: '',
        startDate: null,
        endDate: null,
        frequency: '',
      },

      standingOrderList: [],
      // orderTypeOptions: [
      //   { value: 'CasaAndCasa ', label: 'CASA and CASA' },
      //   { value: 'CasaAndGL', label: 'CASA and GL' },
      //   { value: 'GLANDGL', label: 'GL and GL' },
      // ],
      // transactionTypeOptions: [
      //   { value: 'Flat', label: 'Flat Amount' },
      //   { value: 'Percentage', label: 'Percentage' },
      //   { value: 'FlatAndPercentage', label: 'Flat + Percentage' },
      // ],
      // howLongOptions: [
      //   { value: 'Continuous', label: 'Continuous' },
      //   { value: 'NonContinuous', label: 'Non Continuous' },
      // ],
      // orderFrequencyOptions: [
      //   { value: 'Day', label: 'Days' },
      //   { value: 'Week', label: 'Weeks' },
      //   { value: 'Month', label: 'Months' },
      //   { value: 'Year', label: 'Years' },
      // ],
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        ariaDateFormat: 'd/m/Y',
      },
      isLoadingGlAccount: false,
      accountOrderType: 'Default',
      glAccountOptions: [],
      orderFrequency: null,

      searchParams: {
        customerID: '',
        accountNumber: '',
        accountName: '',
        referenceNumber: '',
      },
      customerList: [],
      tableSearchHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
      ],
      customerSearchPagination: {
        currentPage: 1,
        from: this.customerSearchFrom,
        to: this.customerSearchTo,
        totalEntries: 0,
        perPage: 10,
        pageSize: 0,
      },
      showFormErrorAlert: false,
      customer: {
        accountNumber: '',
      },
      // statusOptions: [
      //   { label: 'Active', value: 'Active' },
      //   { label: 'InActive', value: 'InActive' },
      //   { label: 'Paused', value: 'Paused' },
      //   { label: 'Disabled', value: 'Disabled' },
      //   { label: 'Expired', value: 'Expired' },
      // ],
      accountTypeCasaGl: 'SavingsAndCurrent',
      accountTypeGlCasa: 'GeneralLedgerAccount',
      // accountTypeListOptions: [
      //   { text: 'Savings/Current', value: 'SavingsAndCurrent', disabled: false },
      //   { text: 'General Ledger', value: 'GeneralLedgerAccount', disabled: false },
      // ],
      transactionType: '',
      forHowLongStatus: '',
      standingOrderAmount: '',
      standingOrderPecentage: '',
      standingOrderEndDate: '',
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTableSearchHeaders() {
      return this.tableSearchHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedOrderTypeOptions() {
      return orderTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedTransactionTypeOptions() {
      return transactionTypeOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedHowLongOptions() {
      return howLongOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedOrderFrequencyOptions() {
      return orderFrequencyOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
    translatedAccountTypeListOptions() {
      return accountTypeListOptions.map(item => ({ ...item, text: item.text ? this.$t(item.text) : item.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchStandingOrder()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchStandingOrder()
    },

    accountTypeCasaGl(newValue) {
      if (newValue === 'SavingsAndCurrent') {
        this.accountTypeGlCasa = 'GeneralLedgerAccount'
        this.standingOrderFormData.debitAccount = ''
        this.standingOrderFormData.debitBranchCode = ''
      } else if (newValue === 'GeneralLedgerAccount') {
        this.accountTypeGlCasa = 'SavingsAndCurrent'
        this.standingOrderFormData.creditAccount = ''
        this.standingOrderFormData.creditBranchCode = ''
      }
    },
    accountTypeGlCasa(newValue) {
      if (newValue === 'GeneralLedgerAccount') {
        this.accountTypeCasaGl = 'SavingsAndCurrent'
        this.standingOrderFormData.creditAccount = ''
        this.standingOrderFormData.creditBranchCode = ''
      } else if (newValue === 'SavingsAndCurrent') {
        this.accountTypeCasaGl = 'GeneralLedgerAccount'
        this.standingOrderFormData.debitAccount = ''
        this.standingOrderFormData.debitBranchCode = ''
      }
    },
  },
  mounted() {
    this.fetchStandingOrder()
    this.fetchGLAccount()
  },

  methods: {
    ...mapActions({
      getStandingOrder: 'account/getStandingOrderList',
      getGLAccount: 'account/getAllGLAccount',
      getCustomer: 'account/searchCustomerAccountAction',
      customerAccount: 'account/getCustomerAccountList',
      createStandingOrder: 'account/createStandingOrderAction',
      updateStandingOrder: 'account/updateStandingOrderAction',
      updateStandingOrderStatus: 'account/updateStatus',
    }),
    getNarration() {
      this.standingOrderFormData.creditNarration = this.standingOrderFormData.debitNarration
    },
    setTransactionType(transactionType) {
      this.transactionType = transactionType
      if (transactionType === 'Flat') {
        this.standingOrderAmount = 'required'
        this.standingOrderPecentage = ''
        this.standingOrderFormData.percentageValue = ''
      } else if (transactionType === 'Percentage') {
        this.standingOrderAmount = ''
        this.standingOrderPecentage = 'required'
        this.standingOrderFormData.amount = ''
      } else {
        this.standingOrderAmount = 'required'
        this.standingOrderPecentage = 'required'
        this.standingOrderFormData.amount = ''
        this.standingOrderFormData.percentageValue = ''
      }
    },
    setForHowLong(statusValue) {
      this.forHowLongStatus = statusValue
      if (statusValue === 'Continuous') {
        this.standingOrderEndDate = ''
        this.standingOrderFormData.endDate = ''
      } else {
        this.standingOrderEndDate = 'required'
        this.forHowLongStatus = ''
      }
    },
    setOrderType(type) {
      this.standingOrderFormData.debitAccount = ''
      this.standingOrderFormData.creditAccount = ''
      this.standingOrderFormData.debitBranchCode = ''
      this.standingOrderFormData.creditBranchCode = ''
      this.isDebitCasaBranch = false
      this.isCreditCasaBranch = false

      const orderTypeMap = {
        CasaAndCasa: 'CASA-CASA',
        CasaAndGL: 'CASA-GL',
        GLANDGL: 'GL-GL',
      }
      this.accountOrderType = orderTypeMap[type] || 'Default'
    },

    setOrderFrequency(frequency) {
      const frequencyMap = {
        Day: 1,
        Week: 7,
        Month: getDaysInMonth(frequency),
        Year: getDaysInYear(frequency),
      }
      this.orderFrequency = frequencyMap[frequency] || ''
      this.getComputedTenure(this.orderFrequency)
    },

    getComputedTenure(frequency) {
      if (this.standingOrderFormData.sweepEvery && frequency && this.standingOrderFormData.overHowManyCycles) {
        this.standingOrderFormData.tenure = this.standingOrderFormData.sweepEvery * this.orderFrequency * this.standingOrderFormData.overHowManyCycles
      } else {
        this.standingOrderFormData.tenure = null
      }
    },

    onViewCustomerDebitSearchModal() {
      this.$root.$emit('bv::show::modal', 'customer-debit-search-modal')
    },
    onViewCustomerCreditSearchModal() {
      this.$root.$emit('bv::show::modal', 'customer-credit-search-modal')
    },

    handleDebitSearch() {
      try {
        this.$refs.searchRules.validate().then(async success => {
          if (success) {
            const isSearchParamsNotEmpty = Object.values(
              this.searchParams,
            ).every(params => params === null || params === '')
            if (!isSearchParamsNotEmpty) {
              this.showFormErrorAlert = false
              this.showSpinner = true
              const hasFetchedCustomer = await this.fetchCustomer()
              this.showSpinner = false
              if (hasFetchedCustomer) {
                this.$root.$emit('bv::show::modal', 'search-debit-modal')
              }
            } else {
              this.showFormErrorAlert = true
            }
          }
        })
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    handleCreditSearch() {
      try {
        this.$refs.searchRules.validate().then(async success => {
          if (success) {
            const isSearchParamsNotEmpty = Object.values(
              this.searchParams,
            ).every(params => params === null || params === '')
            if (!isSearchParamsNotEmpty) {
              this.showFormErrorAlert = false
              this.showSpinner = true
              const hasFetchedCustomer = await this.fetchCustomer()
              this.showSpinner = false
              if (hasFetchedCustomer) {
                this.$root.$emit('bv::show::modal', 'search-credit-modal')
              }
            } else {
              this.showFormErrorAlert = true
            }
          }
        })
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },

    async fetchCustomer() {
      try {
        const response = await this.getCustomer({
          search: this.searchParams,
          page: this.currentPage,
          limit: this.perPage,
          permission: 'VIEW_STANDING_ORDERS',
        })

        const { data, status } = response
        if (status === 200 && data.data !== null) {
          this.customerList = data.data.map(item => ({
            ...item,
            customerName: item.name,
            phoneNumber: item.customerType === 'Individual' ? item.phoneNumber : item.businessPhoneNumber,
          }))
          this.customerSearchPagination.totalEntries = data.recordCount
          return true
        }
        this.alertError({ text: this.$t('No record found') })
        return false
      } catch (error) {
        this.alertError({ text: error })
      }
      return false
    },

    async onSelectDebitCustomer(items) {
      this.hideModal('search-debit-modal')
      this.showSpinner = true
      const customerInfo = { ...items[0] }
      this.customer.accountNumber = customerInfo.accountNumber
      this.standingOrderFormData.debitBranchCode = customerInfo.branchCode
      this.isDebitCasaBranch = true
      await this.getLinkedDebitCustomerAccounts()
      this.showCustomerInfo = true
      this.showSearchButton = true
      this.showSpinner = false
    },
    async onSelectCreditCustomer(items) {
      this.hideModal('search-credit-modal')
      this.showSpinner = true
      const customerInfo = { ...items[0] }
      this.customer.accountNumber = customerInfo.accountNumber
      this.standingOrderFormData.creditBranchCode = customerInfo.branchCode
      this.isCreditCasaBranch = true
      await this.getLinkedCreditCustomerAccounts()
      this.showCustomerInfo = true
      this.showSearchButton = true
      this.showSpinner = false
    },

    async getLinkedDebitCustomerAccounts() {
      try {
        this.hideModal('customer-debit-search-modal')
        const response = await this.customerAccount({
          search: { accountNumber: this.customer.accountNumber },
          page: 0,
          limit: 0,
          permission: 'VIEW_STANDING_ORDERS',
        })

        const { data, status } = await response
        if (status === 200 && data.data !== null) {
          this.standingOrderFormData.debitAccount = data.data.reduce((acc, cur) => acc + cur.accountNumber, [])
        } else {
          this.alertError({ text: this.$t('No linked accounts found') })
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getLinkedCreditCustomerAccounts() {
      try {
        this.hideModal('customer-debit-search-modal')
        const response = await this.customerAccount({
          search: { accountNumber: this.customer.accountNumber },
          page: 0,
          limit: 0,
          permission: 'VIEW_STANDING_ORDERS',
        })

        const { data, status } = await response
        if (status === 200 && data.data !== null) {
          this.standingOrderFormData.creditAccount = data.data.reduce((acc, cur) => acc + cur.accountNumber, [])
        } else {
          this.alertError({ text: this.$t('No linked accounts found') })
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    async fetchGLAccount() {
      try {
        this.isLoadingGlAccount = true
        const response = await this.getGLAccount({ permission: accountPermissions.VIEW_STANDING_ORDERS })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.glAccountOptions = data.data.filter(item => item.status === 'Active')
            .reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }],

              // eslint-disable-next-line no-nested-ternary
              []).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingGlAccount = false
      }
    },

    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.standingOrderRules.validate().then(async success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const soText = this.$t('this Standing Order')
          this.confirm(this.standingOrderAction, { text: `${areYouSure} ${this.buttonLabel} ${soText}?` })
        }
      })
    },
    async standingOrderAction() {
      try {
        this.isLoading = true
        const formData = {
          ...this.standingOrderFormData,
          startDate: this.standingOrderFormData.startDate ? toYearWithDate(this.standingOrderFormData.startDate) : null,
          endDate: this.standingOrderFormData.endDate ? toYearWithDate(this.standingOrderFormData.endDate) : null,
          amount: this.standingOrderFormData.amount ? toKobo(this.standingOrderFormData.amount) : 0,
          sweepEvery: this.standingOrderFormData.sweepEvery ? Number(this.standingOrderFormData.sweepEvery) : 0,
          percentageValue: this.standingOrderFormData.percentageValue ? Number(this.standingOrderFormData.percentageValue) : 0,
          overHowManyCycles: this.standingOrderFormData.overHowManyCycles ? Number(this.standingOrderFormData.overHowManyCycles) : 0,
          tenure: this.standingOrderFormData.tenure ? Number(this.standingOrderFormData.tenure) : 0,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createStandingOrder({ formData, permission: accountPermissions.CREATE_STANDING_ORDERS })
          : await this.updateStandingOrder({ formData, permission: accountPermissions.MODIFY_STANDING_ORDERS })
        const { status } = await response
        if (status === 200) {
          this.fetchStandingOrder()
          this.hideModal('modal-lg')
          const requestToText = this.$t('Your request to')
          const text = this.$t('Standing Order was successful')
          this.alertSuccess({ text: `${requestToText} ${this.buttonLabel} ${text}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const requestToText = this.$t('Your request to')
          const reasonText = this.$t('Standing Order failed. Reason')
          this.alertError({ text: `${requestToText} ${this.buttonLabel} ${reasonText}: ${responseError.errorMessage}` })
        })
      } finally {
        this.isLoading = false
      }
    },

    async onSelectStandingOrderItem(items, activity, val) {
      if (val === 'view') {
        this.isViewOnly = true
      }
      this.isEditable = true
      this.standingOrderFormData.type = items.type
      this.standingOrderFormData.debitAccount = items.debitAccount
      this.standingOrderFormData.creditAccount = items.creditAccount
      this.standingOrderFormData.computationType = items.computationType
      this.standingOrderFormData.amount = toBalance(items.amount)
      this.standingOrderFormData.percentageValue = items.percentageValue
      this.standingOrderFormData.forHowLong = items.forHowLong
      this.standingOrderFormData.frequency = items.frequency
      this.standingOrderFormData.sweepEvery = items.sweepEvery
      this.standingOrderFormData.overHowManyCycles = items.overHowManyCycles
      this.standingOrderFormData.tenure = items.tenure
      this.standingOrderFormData.startDate = items.startDate ? toDateWithYear(items.startDate) : ''
      this.standingOrderFormData.endDate = items.endDate ? toDateWithYear(items.endDate) : ''
      this.standingOrderFormData.debitNarration = items.debitNarration
      this.standingOrderFormData.creditNarration = items.creditNarration
      this.standingOrderFormData.debitBranchCode = items.debitBranchCode
      this.standingOrderFormData.creditBranchCode = items.creditBranchCode
      this.standingOrderFormData.description = items.description
      // this.standingOrderFormData.forceDebit = items.forceDebit
      this.standingOrderFormData.id = items.id
      this.isEditable = true
      this.isReadonly = val === 'edit' ? this.isReadonly = false : this.isReadonly = true
      this.modalTitle = val === 'edit' ? this.$t('Edit Standing Order') : this.$t('View Standing Order')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },

    async standingOrderStatus(items) {
      const { id, description } = items
      const actionType = items.status === 'Active' ? 'InActive' : 'Active'
      const action = items.status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = items.status === 'Active' ? 'DEACTIVATE_STANDING_ORDERS' : 'ACTIVATE_STANDING_ORDERS'

      try {
        const updateStatus = async () => {
          this.showSpinner = true
          const statusValues = {
            id,
            name: description,
          }
          const response = await this.updateStandingOrderStatus({
            statusValues, actionType, routeName: 'StandingOrder', permission,
          })
          if (response.status === 200) {
            this.fetchStandingOrder()
            const requestToText = this.$t('Your request to')
            const text = this.$t('Standing Order was successful')
            this.alertSuccess({ text: `${requestToText} ${action} ${items.name} ${text}` })
            this.showSpinner = false
          }
        }
        const areYouSure = this.$t('Are you sure you want to')
        const soText = this.$t('this Standing Order')
        this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${soText}?` })
      } catch (error) {
        const requestToText = this.$t('Your request to')
        const reasonText = this.$t('Standing Order failed. Reason')
        this.alertError({ text: `${requestToText} ${action} ${reasonText}: ${error}` })
      } finally {
        this.showSpinner = false
      }
    },
    async fetchStandingOrder() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const filter = {
          ...this.filter,
          startDate: this.filter.startDate ? toYearWithDate(this.filter.startDate) : null,
          endDate: this.filter.endDate ? toYearWithDate(this.filter.endDate) : null,
        }
        const response = await this.getStandingOrder({
          search: filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_STANDING_ORDERS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.standingOrderList = data.data
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    resetModal() {
      this.standingOrderFormData = { ...null }
      this.isReadonly = false
      this.isLoading = false
      this.isEditable = false
      this.isViewOnly = false
      this.modalTitle = this.$t('Create A Standing Order')
      this.buttonLabel = this.$t('Save')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchStandingOrder()
    },
  },
}
</script>
